/**
 * myarinn Plus: el único plan de pago, igual en la web, en iOS y (cuando
 * llegue) en Android. Aquí vive todo lo que se puede cambiar sin tocar lógica:
 * los topes del plan gratis, los precios de referencia y los textos.
 *
 * Decisiones en docs/estudio-monetizacion.md §7. Cómo se cobra, en
 * docs/monetizacion.md.
 *
 * Quién tiene Plus lo decide tienePlus() en firestore.rules a partir de
 * `usuarios/{uid}` (campos plan*, que solo escribe el servidor) y de
 * `configuracionGlobal/plus` (cuándo empiezan los límites). La función
 * `calcularPlus` de abajo es su copia para la interfaz: si cambias una,
 * cambia la otra.
 */

/** Lo que se puede tener como mucho con el plan gratis. */
export const LIMITES_GRATIS = {
  /** Cuentas corrientes, de ahorro y tarjetas (las huchas van aparte). */
  cuentas: 3,
  huchas: 2,
  /** Gasto + ingreso juntos. */
  categorias: 15,
  presupuestos: 3,
  /** Movimientos y traspasos periódicos, contados juntos. */
  periodicos: 3,
  /** Inversiones activas. 0 = la sección entera es de Plus (decidido el 22/10/2026). */
  inversiones: 0,
  /** Planes de A medias sin archivar que organizas. Los que te invitan no cuentan. */
  planesAMedias: 3,
  /** Casas de Tareas que organizas, sin contar la personal («Mis tareas»). */
  casasCompartidas: 1,
  /**
   * Tareas que se repiten, activas, en una misma casa. Vale el Plus de quien
   * organiza la casa: si lo tiene, toda la casa va sin límite.
   */
  tareasPeriodicas: 10,
};

export type ClaveLimite = keyof typeof LIMITES_GRATIS;

/** Cómo se dice cada límite en una frase: «Con el plan gratis tienes 3 cuentas». */
export const NOMBRE_LIMITE: Record<ClaveLimite, [singular: string, plural: string]> = {
  cuentas: ['cuenta', 'cuentas'],
  huchas: ['hucha', 'huchas'],
  categorias: ['categoría', 'categorías'],
  presupuestos: ['presupuesto', 'presupuestos'],
  periodicos: ['periódico', 'periódicos'],
  inversiones: ['inversión', 'inversiones'],
  planesAMedias: ['plan de A medias', 'planes de A medias'],
  casasCompartidas: ['casa compartida', 'casas compartidas'],
  tareasPeriodicas: ['tarea que se repite por casa', 'tareas que se repiten por casa'],
};

/**
 * Precios de referencia (IVA incluido). Los que se cobran de verdad son los
 * de cada tienda (App Store Connect, Google Play, RevenueCat Web Billing):
 * la hoja de Plus enseña el de la tienda y estos solo si no ha cargado.
 */
export const PRECIOS = {
  mensual: '0,99 €',
  anual: '9,99 €',
  vitalicio: '49,99 €',
} as const;

export type TipoCompra = keyof typeof PRECIOS;

/**
 * Identificadores en RevenueCat. Los paquetes son los estándar de una
 * «offering» ($rc_monthly…), así que en cada tienda el producto puede
 * llamarse como quiera: basta con colgarlo del paquete que toca.
 */
export const REVENUECAT = {
  entitlement: 'plus',
  paquetes: { mensual: '$rc_monthly', anual: '$rc_annual', vitalicio: '$rc_lifetime' } as Record<TipoCompra, string>,
};

/** Lo que trae Plus, para la hoja de venta. */
export const VENTAJAS_PLUS = [
  'Cuentas, huchas y categorías sin límite',
  'Presupuestos y periódicos sin límite',
  'Inversiones: tu cartera, aportaciones y rentabilidad',
  'Préstamos: qué has dejado, a quién y cuándo vuelve',
  'Informes con todo tu histórico: patrimonio, fijos y variables, adelantos y el resumen de cada mes',
  'Apuntar al pagar con Apple Pay: el gasto se abre solo, con la cuenta de cada tarjeta',
  'Importar sin límite de filas',
  'Todos los planes de A medias que quieras organizar, con tickets sin límite y avisos al móvil',
  'Tareas: todas las casas y tareas que se repiten que quieras, para todos los que viven en ella',
  'Tareas por turnos, cómo va el reparto y avisos al móvil de lo que te toca',
  'Tus tareas en el Calendario y en Recordatorios del iPhone',
  'Un solo pago vale en la web, en el iPhone y en el iPad',
];

/** `configuracionGlobal/plus`. Sin documento, los límites no se aplican a nadie. */
export interface ConfigPlus {
  /** Desde cuándo cuentan los límites del plan gratis. */
  limitesDesde: Date;
  /** Hasta cuándo dura el Plus regalado a quien ya tenía cuenta antes de `limitesDesde`. */
  regaloHasta: Date;
}

/** Meses de Plus regalados a los usuarios que ya estaban al activar los límites. */
export const MESES_REGALO = 12;

/** Campos de `usuarios/{uid}` que solo escribe el servidor (o un administrador). */
export interface PlanUsuario {
  /** Fin del periodo pagado. Se renueva solo mientras la suscripción siga viva. */
  planHasta?: Date;
  /** Compra de por vida. */
  planVitalicio?: boolean;
  /** De dónde viene: app_store, play_store, web (RevenueCat Web Billing / Stripe) o admin. */
  planOrigen?: string;
}

export type MotivoPlus = 'admin' | 'sin-limites' | 'pagado' | 'vitalicio' | 'regalo' | 'regalado' | null;

export interface EstadoPlus {
  esPlus: boolean;
  /** Por qué tiene Plus; null si no lo tiene. */
  motivo: MotivoPlus;
  /** Hasta cuándo, si caduca. */
  hasta?: Date;
}

/** Copia de tienePlus() en firestore.rules. */
export function calcularPlus(
  perfil: { rol: string; createdAt: Date } & PlanUsuario,
  config: ConfigPlus | null,
  ahora: Date = new Date(),
): EstadoPlus {
  if (perfil.rol === 'admin') return { esPlus: true, motivo: 'admin' };
  if (perfil.planVitalicio) {
    return { esPlus: true, motivo: perfil.planOrigen === 'admin' ? 'regalado' : 'vitalicio' };
  }
  if (perfil.planHasta && perfil.planHasta > ahora) {
    return { esPlus: true, motivo: perfil.planOrigen === 'admin' ? 'regalado' : 'pagado', hasta: perfil.planHasta };
  }
  if (!config || ahora < config.limitesDesde) return { esPlus: true, motivo: 'sin-limites' };
  if (perfil.createdAt < config.limitesDesde && ahora < config.regaloHasta) {
    return { esPlus: true, motivo: 'regalo', hasta: config.regaloHasta };
  }
  return { esPlus: false, motivo: null };
}

/** Error de «has llegado al tope»: la interfaz lo convierte en la hoja de Plus. */
export class LimiteAlcanzado extends Error {
  constructor(public clave: ClaveLimite) {
    const n = LIMITES_GRATIS[clave];
    super(`Con el plan gratis puedes tener ${n} ${NOMBRE_LIMITE[clave][n === 1 ? 0 : 1]}. Con myarinn Plus, sin límite.`);
    this.name = 'LimiteAlcanzado';
  }
}

/** Lanza LimiteAlcanzado si `usados + nuevos` pasa del tope. */
export function comprobarLimite(clave: ClaveLimite, usados: number, esPlus: boolean, nuevos = 1) {
  if (!esPlus && usados + nuevos > LIMITES_GRATIS[clave]) throw new LimiteAlcanzado(clave);
}

/**
 * Topes del plan gratis que no se cuentan como «cosas que tienes», sino por
 * uso: cuánto historial se ve, cuánto se importa de golpe, cuántas fotos.
 */
export const LIMITES_USO = {
  /** Informes: meses hacia atrás que se pueden ver, contando el actual. */
  mesesInformes: 12,
  /** Filas de un CSV que se importan de una vez. */
  filasImportacion: 200,
  /** Fotos de ticket en un mismo plan de A medias. */
  ticketsPorPlan: 10,
};

/** Días antes del fin del regalo en que la app avisa. */
export const DIAS_AVISO_FIN_REGALO = 30;

/**
 * Al dejar de pagar, los periódicos de más se pausan: solo siguen
 * ejecutándose los LIMITES_GRATIS.periodicos activos más antiguos
 * (movimientos y traspasos juntos). Nada se borra y, al volver a Plus,
 * todos siguen donde estaban. Devuelve los ids de los que corren.
 */
export function periodicosQueCorren(lista: { id: string; createdAt: Date; activo: boolean }[]): Set<string> {
  return new Set(
    lista
      .filter(p => p.activo)
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime() || a.id.localeCompare(b.id))
      .slice(0, LIMITES_GRATIS.periodicos)
      .map(p => p.id),
  );
}
